(ns serv.prompt-markup
  "The markup of an operator prompt (`serv.operator-prompts`), read the same
  way on both ends: the server checks a prompt with it when an agent hands
  one in and checks the answer when it comes back, the window draws it.

  A prompt is Markdown (with GFM: tables, task lists, strikethrough) under a
  YAML frontmatter, and its controls are generic directives:

    ::toggle[Run the migrations]{#migrate}          on or off
    ::option[Production]{group=target value=prod}   one button of a group
    ::input{#version label=\"Version\" required}       one line
    ::text{#notes label=\"Notes\" rows=4}              several lines
    ::upload{#logs label=\"Logs\" accept=\"text/*\" multiple}

  `toggle` and `option` may also stand inside a sentence, with one colon.
  A group's rules are in the frontmatter, `groups: {target: {min: 1, max:
  1}}`; its options can be anywhere in the text. The answer is a map from
  id (or group) to value: a toggle is a boolean, a group with `max: 1` a
  value or nil and any other group a vector of values, an input or text a
  string, an upload a vector of paths.

  The tree is mdast as `mdast-util-from-markdown` makes it - JavaScript
  objects, read as they are."
  (:require ["mdast-util-from-markdown" :refer [fromMarkdown]]
            ["micromark-extension-directive" :refer [directive]]
            ["mdast-util-directive" :refer [directiveFromMarkdown]]
            ["micromark-extension-gfm" :refer [gfm]]
            ["mdast-util-gfm" :refer [gfmFromMarkdown]]
            [clojure.string :as str]))

(defn split-frontmatter
  "`---` YAML `---` and the rest: [yaml body], or [nil text] without one."
  [text]
  (let [text (str/replace (str text) #"\r\n?" "\n")]
    (if-let [[_ y b] (re-matches #"(?s)---\n(.*?)\n?---[ \t]*(?:\n(.*))?" text)]
      [y (or b "")]
      [nil text])))

(defn parse
  "Markdown -> mdast."
  [md]
  (fromMarkdown (str md) #js {:extensions      #js [(directive) (gfm)]
                              :mdastExtensions #js [(directiveFromMarkdown) (gfmFromMarkdown)]}))

(defn plain-text
  "The text in a node, without its markup."
  [^js node]
  (cond (string? (.-value node)) (.-value node)
        (.-children node)        (str/join (map plain-text (.-children node)))
        :else                    ""))

(def kinds #{"toggle" "option" "input" "text" "upload"})

(def ^:private inline-kinds #{"toggle" "option"})

(defn directive? [^js node]
  (contains? #{"leafDirective" "textDirective" "containerDirective"} (.-type node)))

(defn control?
  "A directive this markup knows. A text directive it does not know is just
  text - `:foo` in a sentence, not a mistake."
  [^js node]
  (and (directive? node)
       (contains? kinds (.-name node))
       (or (not= "textDirective" (.-type node)) (contains? inline-kinds (.-name node)))))

(defn- flag
  "An attribute that is there and not `false`."
  [attrs k]
  (and (contains? attrs k) (not= "false" (get attrs k))))

(defn- int-attr [attrs k]
  (let [n (js/parseInt (get attrs k) 10)]
    (when-not (js/isNaN n) n)))

(defn control
  "A directive node -> a control map."
  [^js node]
  (let [attrs (js->clj (or (.-attributes node) #js {}))
        label (str/trim (plain-text node))
        kind  (.-name node)]
    (cond-> {:kind     kind
             :node-type (.-type node)
             :id       (get attrs "id")
             :label    (if (seq label) label (get attrs "label"))
             :required (flag attrs "required")}
      (= "toggle" kind) (assoc :checked (flag attrs "checked"))
      (= "option" kind) (assoc :group    (get attrs "group")
                               :value    (or (get attrs "value") label)
                               :selected (flag attrs "selected"))
      (#{"input" "text"} kind) (assoc :placeholder (get attrs "placeholder")
                                      :value       (get attrs "value"))
      (= "text" kind) (assoc :rows (int-attr attrs "rows"))
      (= "upload" kind) (assoc :accept   (get attrs "accept")
                               :multiple (flag attrs "multiple")))))

(defn- walk [^js node f]
  (f node)
  (doseq [c (or (.-children node) #js [])] (walk c f)))

(defn controls
  "Every control in a tree, in document order. A leaf or container
  directive this markup does not know comes as `{:kind :unknown :name ..}`."
  [tree]
  (let [acc (volatile! [])]
    (walk tree (fn [^js n]
                 (cond (control? n)
                       (vswap! acc conj (control n))

                       (and (directive? n) (not= "textDirective" (.-type n)))
                       (vswap! acc conj {:kind :unknown :name (.-name n)}))))
    @acc))

;;; ------------------------------------------------------------ the prompt

(def id-re #"[A-Za-z][A-Za-z0-9_-]{0,63}")

(defn group-rules
  "A group's rules with the defaults in: one of n, none needed."
  [rules]
  {:min (or (:min rules) 0) :max (or (:max rules) 1)})

(defn- nat? [x] (and (integer? x) (>= x 0)))

(defn check
  "What is wrong with a prompt, as messages; empty when nothing is. `meta`
  is the frontmatter, keys as keywords."
  [meta controls]
  (let [groups   (:groups meta)
        ids      (keep :id (remove #(= "option" (:kind %)) controls))
        options  (filter #(= "option" (:kind %)) controls)
        by-group (group-by :group options)]
    (vec
     (concat
      (for [k [:project-name :subject]
            :when (not (and (string? (get meta k)) (not (str/blank? (get meta k)))))]
        (str "frontmatter: " (name k) " is required"))
      (when (and (some? groups) (not (map? groups)))
        ["frontmatter: groups must be a map"])
      (when (map? groups)
        (for [[g rules] groups
              :let [r (group-rules (when (map? rules) rules))
                    n (count (get by-group (name g)))]
              msg [(when-not (re-matches id-re (name g))
                     (str "group " (name g) ": not a usable name"))
                   (when-not (or (nil? rules) (map? rules))
                     (str "group " (name g) ": its rules must be a map, {min: .., max: ..}"))
                   (when-not (and (nat? (:min r)) (nat? (:max r)) (pos? (:max r)) (<= (:min r) (:max r)))
                     (str "group " (name g) ": needs 0 <= min <= max and max >= 1"))
                   (when (zero? n)
                     (str "group " (name g) ": has no ::option"))
                   (when (< n (:min r))
                     (str "group " (name g) ": min " (:min r) " but only " n " options"))
                   (when (> (count (filter :selected (get by-group (name g)))) (:max r))
                     (str "group " (name g) ": more options selected than max"))]
              :when msg]
          msg))
      (for [{:keys [kind name]} controls :when (= :unknown kind)]
        (str "::" name ": not a control (toggle, option, input, text, upload)"))
      (for [{:keys [kind id]} controls
            :when (and (contains? #{"toggle" "input" "text" "upload"} kind)
                       (not (and id (re-matches id-re id))))]
        (str "::" kind ": needs an id, {#name}"))
      (for [[id n] (frequencies (concat ids (map name (keys (when (map? groups) groups)))))
            :when (> n 1)]
        (str "id " id ": used twice"))
      (for [{:keys [group]} options
            :when (not (contains? (when (map? groups) (set (map name (keys groups)))) group))]
        (str "::option: group " (pr-str group) " is not in the frontmatter's groups"))
      (for [[g opts] by-group
            [v n] (frequencies (map :value opts))
            :when (> n 1)]
        (str "group " g ": value " (pr-str v) " twice"))))))

;;; ------------------------------------------------------------ the answer

(defn fields
  "What the answer is made of: {key {:kind ..}}, each group and each
  control with an id."
  [meta controls]
  (merge
   (into {} (for [[g rules] (:groups meta)]
              [(keyword (name g)) (merge {:kind "group"} (group-rules (when (map? rules) rules)))]))
   (into {} (for [c controls :when (and (:id c) (not= "option" (:kind c)))]
              [(keyword (:id c)) c]))))

(defn defaults
  "The answer before anybody touched anything."
  [meta controls]
  (let [options (filter #(= "option" (:kind %)) controls)]
    (into {}
          (for [[k f] (fields meta controls)]
            [k (case (:kind f)
                 "group"  (let [vs (->> options
                                        (filter #(and (= (name k) (:group %)) (:selected %)))
                                        (mapv :value))]
                            (if (= 1 (:max f)) (first vs) vs))
                 "toggle" (boolean (:checked f))
                 "upload" []
                 (or (:value f) ""))]))))

(def max-text-chars 100000)

(defn check-answer
  "What is wrong with an answer, as messages."
  [meta controls answer]
  (let [options (filter #(= "option" (:kind %)) controls)]
    (vec
     (for [[k f] (fields meta controls)
           :let [v (get answer k)
                 label (name k)
                 msg (case (:kind f)
                       "group"
                       (let [allowed (set (map :value (filter #(= label (:group %)) options)))
                             vs      (cond (nil? v) [] (string? v) [v] (sequential? v) (vec v) :else ::bad)]
                         (cond (= ::bad vs)                         "not a value"
                               (and (= 1 (:max f)) (sequential? v)) "one value, not a list"
                               (not-every? allowed vs)               "a value that is not an option"
                               (not= (count vs) (count (distinct vs))) "a value twice"
                               (< (count vs) (:min f))               (str "at least " (:min f) " needed")
                               (> (count vs) (:max f))               (str "at most " (:max f))))
                       "toggle" (when-not (boolean? v) "on or off")
                       "upload" (cond (not (sequential? v))               "not a list of files"
                                      (and (:required f) (empty? v))      "a file is needed"
                                      (and (not (:multiple f)) (> (count v) 1)) "one file only")
                       (cond (not (string? v))                         "not text"
                             (> (count v) max-text-chars)              "too long"
                             (and (:required f) (str/blank? v))        "is required"))]
           :when msg]
       (str label ": " msg)))))

(defn clean-answer
  "Only what the fields ask for, nothing an answer brought along, and a
  group's value in its one shape."
  [meta controls answer]
  (into {}
        (for [[k f] (fields meta controls)
              :let [v (get answer k)]]
          [k (if (= "group" (:kind f))
               (if (= 1 (:max f))
                 (if (sequential? v) (first v) v)
                 (cond (nil? v) [] (string? v) [v] :else (vec v)))
               v)])))
